/**
 * Upload Files Modal
 * Modal for uploading project files with file type selection
 */

import React, { useState, useRef, useEffect } from 'react';
import { useProjects } from '../../context/ProjectContext';
import { FILE_TYPE, FILE_TYPE_LABELS, FILE_SIZE_LIMITS, WORKFLOW_STATUS } from '../../utils/constants';

// Mapping from workflow status to default file type(s)
// First type in the array is the default
const WORKFLOW_FILE_TYPE_MAP = {
  [WORKFLOW_STATUS.MEASUREMENT_IN_PROGRESS]: FILE_TYPE.SITE_PHOTOS,
  [WORKFLOW_STATUS.MEASUREMENT_DONE]: FILE_TYPE.SITE_PHOTOS,
  [WORKFLOW_STATUS.TWO_D_PENDING]: FILE_TYPE.TWO_D_DRAWING,
  [WORKFLOW_STATUS.TWO_D_IN_PROGRESS]: FILE_TYPE.TWO_D_DRAWING,
  [WORKFLOW_STATUS.TWO_D_PENDING_CLIENT_REVIEW]: FILE_TYPE.TWO_D_DRAWING,
  [WORKFLOW_STATUS.TWO_D_AMENDMENT_REQUIRED]: FILE_TYPE.TWO_D_DRAWING,
  [WORKFLOW_STATUS.THREE_D_PENDING]: FILE_TYPE.THREE_D_RENDER,
  [WORKFLOW_STATUS.THREE_D_IN_PROGRESS]: FILE_TYPE.THREE_D_RENDER,
  [WORKFLOW_STATUS.THREE_D_PENDING_CLIENT_REVIEW]: FILE_TYPE.THREE_D_RENDER,
  [WORKFLOW_STATUS.THREE_D_AMENDMENT_REQUIRED]: FILE_TYPE.THREE_D_RENDER,
  [WORKFLOW_STATUS.DD_PENDING]: FILE_TYPE.DD_SPECS,
  [WORKFLOW_STATUS.DD_IN_PROGRESS]: FILE_TYPE.DD_SPECS,
  [WORKFLOW_STATUS.DD_SUBMITTED_TO_QUOTATION]: FILE_TYPE.DD_SPECS,
  [WORKFLOW_STATUS.QUOTATION_IN_PROGRESS]: FILE_TYPE.QUOTATION,
  [WORKFLOW_STATUS.READY_FOR_SIGNING]: FILE_TYPE.CONTRACT,
  [WORKFLOW_STATUS.SIGNING_SCHEDULED]: FILE_TYPE.CONTRACT,
  [WORKFLOW_STATUS.SIGNING_AMENDMENT_REQUIRED]: FILE_TYPE.CONTRACT,
};

// Get default file type based on workflow status
const getDefaultFileType = (workflowStatus, allowedFileTypes) => {
  // If allowedFileTypes is specified, use the first one
  if (allowedFileTypes && allowedFileTypes.length > 0) {
    return allowedFileTypes[0];
  }

  // Use workflow-based default, or fall back to OTHER
  return WORKFLOW_FILE_TYPE_MAP[workflowStatus] || FILE_TYPE.OTHER;
};

const UploadFilesModal = ({ isOpen, onClose, projectId, onSuccess, allowedFileTypes = null, workflowStatus = null }) => {
  const { uploadProjectFile } = useProjects();

  const [selectedFiles, setSelectedFiles] = useState([]);
  // Default based on workflow status, then allowed types, then OTHER
  const [fileType, setFileType] = useState(
    getDefaultFileType(workflowStatus, allowedFileTypes)
  );

  // Get file types to show in dropdown
  const availableFileTypes = allowedFileTypes
    ? Object.entries(FILE_TYPE).filter(([key, value]) => allowedFileTypes.includes(value))
    : Object.entries(FILE_TYPE);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({});
  const [dragActive, setDragActive] = useState(false);
  const [errors, setErrors] = useState([]);

  const fileInputRef = useRef(null);

  // Reset file type when modal opens or workflow status changes
  useEffect(() => {
    if (isOpen) {
      setFileType(getDefaultFileType(workflowStatus, allowedFileTypes));
    }
  }, [isOpen, workflowStatus, allowedFileTypes]);

  if (!isOpen) return null;

  // Get max file size for selected type
  const maxFileSize = FILE_SIZE_LIMITS[fileType] || FILE_SIZE_LIMITS[FILE_TYPE.OTHER];

  // Format file size
  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  // Validate file
  const validateFile = (file) => {
    const errors = [];

    if (file.size > maxFileSize) {
      errors.push(`File size exceeds limit (${formatFileSize(maxFileSize)})`);
    }

    return errors;
  };

  // Handle file selection
  const handleFileSelect = (files) => {
    const newFiles = [];
    const newErrors = [];

    Array.from(files).forEach(file => {
      const fileErrors = validateFile(file);
      if (fileErrors.length > 0) {
        newErrors.push({ name: file.name, errors: fileErrors });
      } else {
        newFiles.push(file);
      }
    });

    if (newErrors.length > 0) {
      setErrors(newErrors);
    } else {
      setErrors([]);
    }

    setSelectedFiles(prev => [...prev, ...newFiles]);
  };

  // Handle drag events
  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  // Handle drop
  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      handleFileSelect(e.dataTransfer.files);
    }
  };

  // Remove file from list
  const removeFile = (index) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
  };

  // Upload files
  const handleUpload = async () => {
    if (selectedFiles.length === 0) return;

    setUploading(true);
    setErrors([]);

    try {
      const uploadPromises = selectedFiles.map(async (file, index) => {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('projectId', projectId);
        formData.append('fileType', fileType);

        try {
          // Simulate progress (since uploadProjectFile doesn't expose progress)
          setUploadProgress(prev => ({ ...prev, [index]: 50 }));

          const result = await uploadProjectFile(formData);

          setUploadProgress(prev => ({ ...prev, [index]: 100 }));
          return { success: true, file, data: result };
        } catch (error) {
          return { success: false, file, error: error.message || 'Upload failed' };
        }
      });

      const results = await Promise.all(uploadPromises);

      // Check for failures
      const failures = results.filter(r => !r.success);
      if (failures.length > 0) {
        setErrors(failures.map(f => ({ name: f.file.name, errors: [f.error] })));
      }

      // Call success callback with successful uploads
      const successes = results.filter(r => r.success);
      if (successes.length > 0) {
        if (onSuccess) {
          onSuccess(successes.map(s => s.data));
        }

        // Close modal on full success
        if (failures.length === 0) {
          handleClose();
        }
      }
    } catch (error) {
      console.error('Upload error:', error);
      setErrors([{ name: 'General', errors: ['Failed to upload files'] }]);
    } finally {
      setUploading(false);
      setUploadProgress({});
    }
  };

  // Reset and close
  const handleClose = () => {
    setSelectedFiles([]);
    setFileType(getDefaultFileType(workflowStatus, allowedFileTypes));
    setErrors([]);
    setUploadProgress({});
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
          <h2 className="text-xl font-semibold text-gray-900">Upload Files</h2>
          <button
            onClick={handleClose}
            disabled={uploading}
            className="text-gray-400 hover:text-gray-600 disabled:opacity-50"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Body */}
        <div className="p-6 space-y-6">
          {/* File Type Selector */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              File Type *
            </label>
            <select
              value={fileType}
              onChange={(e) => setFileType(e.target.value)}
              disabled={uploading}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:opacity-50"
            >
              {availableFileTypes.map(([key, value]) => (
                <option key={value} value={value}>
                  {FILE_TYPE_LABELS[value]}
                </option>
              ))}
            </select>
            <p className="text-sm text-gray-500 mt-1">
              Max file size: {formatFileSize(maxFileSize)}
            </p>
          </div>

          {/* Drop zone */}
          <div
            className={`relative border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
              dragActive
                ? 'border-blue-500 bg-blue-50'
                : 'border-gray-300 hover:border-gray-400'
            } ${uploading ? 'opacity-50 pointer-events-none' : ''}`}
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
          >
            <input
              ref={fileInputRef}
              type="file"
              multiple
              onChange={(e) => handleFileSelect(e.target.files)}
              className="hidden"
              disabled={uploading}
            />

            <div className="space-y-4">
              {/* Icon */}
              <div className="flex justify-center">
                <svg className="w-16 h-16 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                </svg>
              </div>

              {/* Text */}
              <div>
                <p className="text-lg font-medium text-gray-900">Drop files here or click to browse</p>
                <p className="text-sm text-gray-500 mt-1">
                  Select {FILE_TYPE_LABELS[fileType]} files
                </p>
              </div>

              {/* Browse button */}
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium disabled:opacity-50"
              >
                Browse Files
              </button>
            </div>
          </div>

          {/* Error messages */}
          {errors.length > 0 && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <h4 className="font-medium text-red-800 mb-2">Upload Errors:</h4>
              <ul className="list-disc list-inside space-y-1">
                {errors.map((error, index) => (
                  <li key={index} className="text-sm text-red-700">
                    <strong>{error.name}:</strong> {error.errors.join(', ')}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* File list */}
          {selectedFiles.length > 0 && (
            <div className="space-y-3">
              <h4 className="font-medium text-gray-900">Selected Files ({selectedFiles.length})</h4>
              {selectedFiles.map((file, index) => (
                <div key={index} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    {/* File icon */}
                    <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                      </svg>
                    </div>

                    {/* File info */}
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-gray-900 truncate">{file.name}</p>
                      <p className="text-sm text-gray-500">{formatFileSize(file.size)}</p>

                      {/* Upload progress */}
                      {uploading && uploadProgress[index] !== undefined && (
                        <div className="mt-2">
                          <div className="w-full bg-gray-200 rounded-full h-2">
                            <div
                              className="bg-blue-600 h-2 rounded-full transition-all"
                              style={{ width: `${uploadProgress[index]}%` }}
                            />
                          </div>
                          <p className="text-xs text-gray-500 mt-1">{uploadProgress[index]}%</p>
                        </div>
                      )}
                    </div>

                    {/* Remove button */}
                    {!uploading && (
                      <button
                        onClick={() => removeFile(index)}
                        className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-gray-50 border-t border-gray-200 px-6 py-4 flex items-center justify-end gap-3">
          <button
            onClick={handleClose}
            disabled={uploading}
            className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={handleUpload}
            disabled={uploading || selectedFiles.length === 0}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {uploading ? (
              <span className="flex items-center gap-2">
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                Uploading...
              </span>
            ) : (
              `Upload ${selectedFiles.length} File${selectedFiles.length !== 1 ? 's' : ''}`
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default UploadFilesModal;
